import supabase from '../supabase/supabase';

const CASH_ACCOUNT_PATTERN = /(cash|bank|checking|savings|petty cash|money market)/i;
const INVESTING_PATTERN = /(equipment|vehicle|property|building|fixed asset|investment|land)/i;
const FINANCING_PATTERN = /(loan|credit card|credit line|note payable|mortgage|debt)/i;
const PAYROLL_PATTERN = /(payroll|salary|wage|tax withholding|employee)/i;
const NON_POSTED_STATUSES = ['void', 'voided', 'cancelled', 'canceled', 'draft', 'pending'];

const round = (value) => Number(Number(value || 0).toFixed(2));

const isPostedTransaction = (transaction) => (
  !NON_POSTED_STATUSES.includes(String(transaction?.status || '').trim().toLowerCase())
);

const isCashAccount = (account) => {
  const type = String(account?.type || '').toLowerCase();
  const subType = String(account?.sub_type || '');
  const name = String(account?.name || '');

  return type === 'asset' && CASH_ACCOUNT_PATTERN.test(`${name} ${subType}`);
};

const getAccountBalance = (account, transactions) => {
  const total = transactions
    .filter((transaction) => transaction.coa_id === account.id)
    .reduce(
      (sum, transaction) => sum + Number(transaction.debit || 0) - Number(transaction.credit || 0),
      0
    );

  return round(total);
};

const getActivityGroup = (account) => {
  const type = String(account?.type || '').toLowerCase();
  const text = `${account?.name || ''} ${account?.sub_type || ''}`;

  if (INVESTING_PATTERN.test(text)) {
    return {
      section: 'investing',
      group: text.match(/vehicle/i) ? 'Vehicles' : text.match(/investment/i) ? 'Investments' : 'Equipment'
    };
  }

  if (type === 'equity') {
    return {
      section: 'financing',
      group: /draw/i.test(text) ? 'Owner Draws' : 'Owner Contributions'
    };
  }

  if (type === 'liability' || FINANCING_PATTERN.test(text)) {
    return {
      section: 'financing',
      group: /payment|credit card|credit line/i.test(text) ? 'Debt Payments' : 'Loans and Lines of Credit'
    };
  }

  if (PAYROLL_PATTERN.test(text)) {
    return { section: 'operating', group: 'Payroll' };
  }

  if (type === 'income' || type === 'revenue') {
    return { section: 'operating', group: 'Sales' };
  }

  if (type === 'expense' || /cost of sales|cost of goods/i.test(text)) {
    return { section: 'operating', group: 'Purchases' };
  }

  return { section: 'operating', group: 'Other' };
};

const createGroup = () => ({
  total: 0,
  accounts: []
});

const createSection = () => ({
  total: 0,
  groups: {}
});

const addCashMovement = (section, groupName, account, amount) => {
  if (!section.groups[groupName]) section.groups[groupName] = createGroup();

  const group = section.groups[groupName];
  let detail = group.accounts.find((item) => item.id === account.id);

  if (!detail) {
    detail = {
      id: account.id,
      code: account.code || '',
      name: account.name,
      amount: 0
    };
    group.accounts.push(detail);
  }

  detail.amount = round(detail.amount + amount);
  group.total = round(group.total + amount);
  section.total = round(section.total + amount);
};

const buildCashMovement = (cashLine, offsetAccount, amount, report) => {
  if (!offsetAccount || isCashAccount(offsetAccount)) return;

  const { section, group } = getActivityGroup(offsetAccount);
  addCashMovement(report[section], group, offsetAccount, amount);
};

const getPeriodTransactions = (transactions, startDate, endDate) => (
  transactions.filter((transaction) => (
    transaction.date >= startDate &&
    transaction.date <= endDate &&
    isPostedTransaction(transaction)
  ))
);

const getCashAccounts = (coa) => coa.filter((account) => (
  account.is_active !== false && isCashAccount(account)
));

export const cashFlowReportService = {
  async generate(startDate, endDate, accountId) {
    if (!accountId) throw new Error('An active business is required.');

    const { data: coa, error: coaError } = await supabase
      .schema('accounting')
      .from('chart_of_accounts_20240522')
      .select('*')
      .eq('account_id', accountId)
      .eq('is_active', true);

    if (coaError) throw coaError;

    const { data: transactions, error: transactionError } = await supabase
      .schema('accounting')
      .from('transactions_20240522')
      .select('*')
      .eq('account_id', accountId)
      .order('date', { ascending: true })
      .order('created_at', { ascending: true });

    if (transactionError) throw transactionError;

    const accounts = coa || [];
    const ledger = (transactions || []).filter(isPostedTransaction);
    const cashAccounts = getCashAccounts(accounts);
    const periodTransactions = getPeriodTransactions(ledger, startDate, endDate);
    const previousDate = new Date(`${startDate}T00:00:00`);
    previousDate.setDate(previousDate.getDate() - 1);
    const previousDateValue = previousDate.toISOString().split('T')[0];

    const report = {
      operating: createSection(),
      investing: createSection(),
      financing: createSection(),
      beginningCash: round(cashAccounts.reduce(
        (sum, account) => sum + getAccountBalance(
          account,
          ledger.filter((transaction) => transaction.date <= previousDateValue)
        ),
        0
      )),
      endingCash: round(cashAccounts.reduce(
        (sum, account) => sum + getAccountBalance(
          account,
          ledger.filter((transaction) => transaction.date <= endDate)
        ),
        0
      ))
    };

    const transactionsByBatch = new Map();

    periodTransactions.forEach((transaction) => {
      const batchId = transaction.batch_id || transaction.id;
      if (!transactionsByBatch.has(batchId)) transactionsByBatch.set(batchId, []);
      transactionsByBatch.get(batchId).push(transaction);
    });

    transactionsByBatch.forEach((batch) => {
      const cashLines = batch.filter((transaction) => (
        cashAccounts.some((account) => account.id === transaction.coa_id)
      ));

      cashLines.forEach((cashLine) => {
        const cashEffect = Number(cashLine.debit || 0) - Number(cashLine.credit || 0);
        const offsetLines = batch.filter((transaction) => transaction.id !== cashLine.id);

        offsetLines.forEach((offsetLine) => {
          const offsetAccount = accounts.find((account) => account.id === offsetLine.coa_id);
          const offsetAmount = Math.abs(Number(offsetLine.debit || 0) - Number(offsetLine.credit || 0));

          if (offsetLines.length === 1) {
            buildCashMovement(cashLine, offsetAccount, cashEffect, report);
          } else if (offsetAmount > 0) {
            const proportion = Math.abs(cashEffect) === 0
              ? 0
              : offsetAmount / Math.abs(cashEffect);
            buildCashMovement(cashLine, offsetAccount, cashEffect * proportion, report);
          }
        });
      });
    });

    report.classifiedNetChange = round(
      report.operating.total + report.investing.total + report.financing.total
    );
    report.netChange = round(report.endingCash - report.beginningCash);
    report.unclassifiedCashChange = round(report.netChange - report.classifiedNetChange);
    report.grossInflow = round(
      [report.operating, report.investing, report.financing]
        .flatMap((section) => Object.values(section.groups))
        .reduce((sum, group) => sum + Math.max(group.total, 0), 0)
    );
    report.grossOutflow = round(
      [report.operating, report.investing, report.financing]
        .flatMap((section) => Object.values(section.groups))
        .reduce((sum, group) => sum + Math.abs(Math.min(group.total, 0)), 0)
    );

    return report;
  }
};